import { Button, ListItemText, Menu, MenuItem } from '@mui/material';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { usePatientsGetById } from '~/backend/connector-next-api/connector-next-components';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { casesOf, scopeToOpen } from '~/containers/patient-list/model';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { openedScopeState } from '~/hooks/openInViewer/openedScopeAtom';
import type { OpenIntent } from '~/hooks/openInViewer/resolveOpenIntent';
import { useReopenPatient } from '~/hooks/openInViewer/useReopenPatient';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';

/** A switch the user has asked for: a case's id, or null for every object. */
type Target = { caseId: string | null };

/**
 * Names what the loaded connector patient was opened as — a case, a selection
 * of objects, or every object — and switches to one of its cases, or to every
 * object, by re-opening the patient as it.
 * Shown only when the patient has cases. Switching
 * reloads the patient, so unsaved changes are put to the user first rather
 * than dropped, and a case that cannot be opened is shown with the reason and
 * not offered: the patient would be unloaded for a switch that cannot happen.
 */
export default function CaseSwitcher() {
    const intl = useIntl();
    const session = useSessionOrNull();
    const patientId = session?.persistenceRegistry.patientRef?.patientId;
    const { data } = usePatientsGetById(
        { pathParams: { id: patientId ?? '' } },
        { enabled: !!patientId }
    );
    const openedScope = useRecoilValue(openedScopeState);
    const openedCaseId =
        openedScope !== null && 'caseId' in openedScope
            ? openedScope.caseId
            : null;
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesState);
    const reopen = useReopenPatient();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [pending, setPending] = useState<Target>();

    const cases = casesOf(data, { archived: false });
    if (!patientId || (data?.cases?.length ?? 0) === 0) {
        return null;
    }

    const current = data?.cases?.find((c) => c.id === openedCaseId);
    const label =
        openedScope === null
            ? intl.formatMessage({ id: 'caseSwitcher.allObjects' })
            : 'datasetIds' in openedScope
              ? intl.formatMessage(
                    { id: 'caseSwitcher.selection' },
                    { count: openedScope.datasetIds.length }
                )
              : (current?.name ??
                intl.formatMessage({ id: 'caseSwitcher.caseGone' }));

    const switchTo = ({ caseId }: Target) => {
        const intent: OpenIntent | undefined =
            caseId === null ? undefined : { scope: { caseId } };
        reopen(intent);
    };

    const choose = (target: Target) => {
        setMenuAnchor(null);
        const isOpen =
            target.caseId === null
                ? openedScope === null
                : target.caseId === openedCaseId;
        if (isOpen) {
            return;
        }
        if (hasUnsavedChanges) {
            setPending(target);
        } else {
            switchTo(target);
        }
    };

    return (
        <>
            <Button
                size="small"
                color="inherit"
                endIcon={<ChevronDown size={14} />}
                aria-haspopup="menu"
                aria-label={intl.formatMessage(
                    { id: 'caseSwitcher.label' },
                    { name: label }
                )}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={{ textTransform: 'none' }}
            >
                <SensitiveInfoTypography>{label}</SensitiveInfoTypography>
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuAnchor !== null}
                onClose={() => setMenuAnchor(null)}
                MenuListProps={{ dense: true }}
            >
                <MenuItem
                    selected={openedScope === null}
                    onClick={() => choose({ caseId: null })}
                >
                    {intl.formatMessage({ id: 'caseSwitcher.allObjects' })}
                </MenuItem>
                {cases.map((c) => {
                    const hasNoImage =
                        data !== undefined &&
                        !!c.id &&
                        'refusal' in scopeToOpen(data, { caseId: c.id });
                    return (
                        <MenuItem
                            key={c.id}
                            selected={c.id === openedCaseId}
                            disabled={hasNoImage}
                            onClick={() => choose({ caseId: c.id ?? null })}
                        >
                            <ListItemText
                                primary={
                                    <SensitiveInfoTypography>
                                        {c.name}
                                    </SensitiveInfoTypography>
                                }
                                secondary={
                                    hasNoImage
                                        ? intl.formatMessage({
                                              id: 'caseSwitcher.caseHasNoImage',
                                          })
                                        : undefined
                                }
                            />
                            {c.id === data?.workCaseId && (
                                <ArrowRight
                                    size={14}
                                    aria-label={intl.formatMessage({
                                        id: 'patientList.cases.workCase',
                                    })}
                                    style={{ marginLeft: 8 }}
                                />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
            <ConfirmationDialog
                open={pending !== undefined}
                mode="warning"
                title={intl.formatMessage({ id: 'caseSwitcher.confirmTitle' })}
                message={intl.formatMessage({
                    id: 'caseSwitcher.confirmUnsaved',
                })}
                confirmText={intl.formatMessage({
                    id: 'switchWithoutSaving',
                })}
                onCancel={() => setPending(undefined)}
                onConfirm={() => {
                    if (pending !== undefined) {
                        switchTo(pending);
                    }
                    setPending(undefined);
                }}
            />
        </>
    );
}
